// Dashboard service. Fetches aggregated stats for teacher and school admin dashboards.
import { apiGet } from './apiClient';

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface TeacherStats {
  studentsMonitored: number;
  activeAlerts: number;
  incidentsPrevented: number;
  avgResponseTime: number; // minutes
}

export interface Alert {
  id: string;
  studentName: string;
  alertType: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'new' | 'acknowledged' | 'investigating' | 'resolved' | 'escalated' | 'dismissed';
  assignedTo: { id: string; name: string } | null;
  createdAt: string;
}

export interface MonitoredStudent {
  id: string;
  name: string;
  email: string;
  riskScore: number;
  lastActive: string | null;
  alertsThisMonth: number;
  isFlagged?: boolean;
  flagAlertId?: string | null;
  currentDomain?: string | null;
  currentEventType?: string | null;
  currentPageTitle?: string | null;
  currentUrl?: string | null;
  isActive?: boolean;
}

export interface SchoolAdminStats {
  totalStudents: number;
  totalTeachers: number;
  activeIncidents: number;
  safetyScore: number;
}

export interface SuperAdminStats {
  totalSchools: number;
  totalUsers: number;
  activeIncidents: number;
  detectionRate: number;
}

export interface SchoolWithStats {
  id: string;
  name: string;
  contactEmail: string;
  isActive: boolean;
  subscriptionTier: string;
  totalUsers: number;
  totalAlerts: number;
  createdAt: string;
}

export interface DashboardActivity {
  id: string;
  type: 'incident' | 'school' | 'system';
  message: string;
  status?: string;
  timestamp: string;
}

export const dashboardService = {
  // Teacher Endpoints
  async getTeacherStats(): Promise<TeacherStats> {
    return apiGet<TeacherStats>('/dashboard/teacher/stats');
  },

  async getTeacherAlerts(page: number = 1, limit: number = 20): Promise<PaginatedResponse<Alert>> {
    return apiGet<PaginatedResponse<Alert>>(`/alerts?page=${page}&limit=${limit}`);
  },

  async getMonitoredStudents(page: number = 1, limit: number = 20): Promise<PaginatedResponse<MonitoredStudent>> {
    return apiGet<PaginatedResponse<MonitoredStudent>>(`/dashboard/teacher/students?page=${page}&limit=${limit}`);
  },

  // Admin Endpoints
  async getSchoolAdminStats(): Promise<SchoolAdminStats> {
    return apiGet<SchoolAdminStats>('/dashboard/admin/stats');
  },

  // Super Admin Endpoints (company-level)
  async getSuperAdminStats(): Promise<SuperAdminStats> {
    return apiGet<SuperAdminStats>('/dashboard/super-admin/stats');
  },

  async getAllSchoolsWithStats(page: number = 1, limit: number = 20): Promise<PaginatedResponse<SchoolWithStats>> {
    return apiGet<PaginatedResponse<SchoolWithStats>>(`/schools?page=${page}&limit=${limit}`);
  },

  async getDashboardActivity(page: number = 1, limit: number = 20): Promise<PaginatedResponse<DashboardActivity>> {
    return apiGet<PaginatedResponse<DashboardActivity>>(`/dashboard/activity?page=${page}&limit=${limit}`);
  },

  async getMySchool(): Promise<{ id: string; name: string; city: string | null; state: string | null }> {
    const res = await apiGet<{ success: boolean; school: { id: string; name: string; city: string | null; state: string | null } }>('/schools/my-school');
    return res.school;
  },
};
